import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { clickWiseAttributionOptions, saleByWidgetFilterOptions } from 'src/assets/AnalyticsConfig'
import { PieChart } from 'src/components/analytics/charts/AnimatedPie'
import { NoData } from 'src/components/analytics/common/NoData'
import DropdownButton from 'src/components/common/form-elements/DropdownButton'
import { useAnalytics } from 'src/lib/contexts/AnalyticsContext'
import { postRequest } from 'src/lib/core/api-client'
import { ClickWiseAttributionsType, OverviewWidgetFilterOptionsType } from 'src/lib/types/dashboardAnalytics'
import { apiURI } from 'src/lib/utils/constants'
import { AnalyticsFilters } from '../../useChartData'
import { ChartSkeleton } from '../common/Skeleton'

const fetchData: any = (endPoint: string, currentFilters: AnalyticsFilters & { utmKey: string }) => {
  const request = postRequest(`/dashboard/widget${endPoint}`, apiURI.CHECKOUT_NODE_V2, currentFilters)
  return request
}

const FIRST_CLICK_ENDPOINT = '/orders-utm-first-click'
const LAST_CLICK_ENDPOINT = '/orders-utm-last-click'

const MODE_OPTIONS = [
  {
    label: 'Percentage of sales',
    value: 'percentage'
  },
  {
    label: 'Number of sales',
    value: 'number'
  }
]

const formatter = (value: number | string) => {
  return typeof value === 'number'
    ? value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : `${value}%`
}

const PIE_COLORS = ['#4EC9B4', '#7B7FEA', '#8DD36F', '#D15BA6', '#8B3A36', '#8E3FC8']

const SalesBreakDownByUTMPieChart = () => {
  const {
    getters: { filters, refreshKey }
  } = useAnalytics()
  const { ref, inView } = useInView({
    threshold: 0,
    triggerOnce: true
  })
  const [selectedAttributionType, setSelectedAttributionType] = useState<ClickWiseAttributionsType>(
    clickWiseAttributionOptions[0]
  )
  const [selectedWidgetFilterType, setSelectedWidgetFilterType] = useState<OverviewWidgetFilterOptionsType>(
    saleByWidgetFilterOptions[0]
  )
  const [selectedMode, setSelectedMode] = useState<string>(MODE_OPTIONS[0].value)

  const parsedFilters = useMemo(() => {
    return {
      ...filters,
      utmKey: selectedWidgetFilterType.value
    }
  }, [filters, selectedWidgetFilterType])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['salesBreakDownByUTM', refreshKey, JSON.stringify(parsedFilters), selectedAttributionType.value],
    queryFn: () => {
      return fetchData(
        selectedAttributionType.value === 'FIRST_CLICK' ? FIRST_CLICK_ENDPOINT : LAST_CLICK_ENDPOINT,
        parsedFilters
      )
    },
    enabled: inView
  })

  const pieData = useMemo(() => {
    if (!data) return []
    const rawData: {
      utm_value: string
      orders_count: number
      revenue_percentage: number
    }[] = data?.data?.result?.widgetData?.data?.data
    const mappedData = rawData?.map((item, idx) => ({
      label: item.utm_value,
      value: selectedMode === 'percentage' ? item.revenue_percentage.toLocaleString() : item.orders_count,
      color: PIE_COLORS[idx % PIE_COLORS.length],
      change: 0
    }))
    return mappedData
  }, [data, selectedMode])

  const renderComponent = () => {
    if (isLoading) {
      return <ChartSkeleton />
    } else if (isError) {
      return <div>Error</div>
    } else if (pieData.length > 0) {
      return (
        <>
          <div className='flex items-center gap-2 mt-4 mb-3'>
            <DropdownButton
              options={clickWiseAttributionOptions.map(option => ({
                value: option,
                label: option.label
              }))}
              size='large'
              variant='outlined'
              selectedOption={clickWiseAttributionOptions.findIndex(
                option => option.value === selectedAttributionType.value
              )}
              handleClick={(index, value) => setSelectedAttributionType(clickWiseAttributionOptions[index])}
            />
            <DropdownButton
              size='large'
              variant='outlined'
              options={saleByWidgetFilterOptions.map(option => ({
                value: option,
                label: option.label
              }))}
              selectedOption={saleByWidgetFilterOptions.findIndex(
                option => option.value === selectedWidgetFilterType.value
              )}
              handleClick={(index, value) => setSelectedWidgetFilterType(saleByWidgetFilterOptions[index])}
            />
            <DropdownButton
              size='large'
              variant='outlined'
              options={MODE_OPTIONS.map(option => ({
                value: option.value,
                label: option.label
              }))}
              selectedOption={MODE_OPTIONS.findIndex(option => option.value === selectedMode)}
              handleClick={(index, value) => setSelectedMode(MODE_OPTIONS[index].value)}
            />
          </div>
          {pieData.length > 0 ? (
            <div className='w-full h-[90%] flex justify-center items-center'>
              <PieChart data={pieData} formatter={formatter} colors={PIE_COLORS} />
            </div>
          ) : (
            <NoData />
          )}
        </>
      )
    } else {
      return <NoData />
    }
  }

  return (
    <div ref={ref} className='w-full h-full'>
      {renderComponent()}
    </div>
  )
}

export default SalesBreakDownByUTMPieChart
